import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Loader2, RefreshCw, AlertTriangle, CheckCircle2, FileSpreadsheet, FileText,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * FirstSaleVendors — CHA-362. Imports → First Sale → Vendors: the First Sale program list
 * (from `lookup_first_sale_vendors`, editable in Admin → Lookup Manager) joined by the vendor's
 * BC code(s) (= ERP lc_beneficiary) against the SP27 First Sale book — CHA-424: FS/FS3 terms only,
 * from 2026-07-01 onward, with OPEN (by ship date) and RECEIVED (by receipt date) shown separately:
 *  - per-vendor: program status, approved?, letter filed?, open + received orders / units / FOB, total;
 *  - compliance lives in Production → Verification (CHA-363), including the 'missing-fs' gap this
 *    screen deliberately no longer shows.
 */

const money = (v) => (v == null ? '—' : `$${Math.round(Number(v)).toLocaleString()}`);

// ─────────────────────────── CHA-430 — Excel / PDF export ───────────────────────────
// The row builders are pure and named-exported so they can be unit-tested without a DOM or a
// spreadsheet library, following this tab's existing convention (verdictFor in FirstSaleBeneficiaries).
//
// MONEY AND COUNTS STAY NUMERIC. The on-screen money() renders '$1,234' / '—', which lands in Excel as
// TEXT — the recipient then cannot sum a column, which is most of the reason to want a spreadsheet.
// null is preserved as null (an empty cell) rather than coerced to 0, so "no data" and "zero dollars"
// stay distinguishable.
const numOrNull = (v) => (v == null ? null : Number(v));
const yesNo = (v) => (v ? 'Yes' : 'No');

// The Vendors sheet's column contract. The Excel button is deliberately enabled when the roster
// filters to nothing but outside-program volume exists, so json_to_sheet([]) — which yields a
// HEADERLESS sheet — is reachable in a supported state. A downstream import then fails on exactly the
// export we chose to allow (Codex P2). Pinned to the builder's keys by a test.
export const VENDOR_COLUMNS = [
  'Vendor', 'Status', 'Approved', 'Letter filed', 'BC code(s)', 'Code conflicts',
  'Open orders', 'Open units', 'Open FOB', 'Received orders', 'Received units', 'Received FOB',
  'Total FOB', 'Comments',
];

export function vendorExportRows(vendors) {
  return (vendors || []).map((v) => ({
    Vendor: v.vendor_name || '',
    Status: v.status || '',
    Approved: yesNo(v.approved),
    'Letter filed': yesNo(v.letter_filed),
    'BC code(s)': (v.bc_codes || []).join(', '),
    // The screen shows this as a hoverable badge; in a file it has to be a column or it is lost.
    'Code conflicts': (v.code_conflicts || []).join('; '),
    'Open orders': numOrNull(v.open_orders),
    'Open units': numOrNull(v.open_units),
    'Open FOB': numOrNull(v.open_fob),
    'Received orders': numOrNull(v.recd_orders),
    'Received units': numOrNull(v.recd_units),
    'Received FOB': numOrNull(v.recd_fob),
    'Total FOB': numOrNull(v.total_fob),
    Comments: v.comments || '',
  }));
}

export function unclaimedExportRows(unclaimed) {
  return (unclaimed || []).map((u) => ({
    'BC code': u.benef || '',
    'Open orders': numOrNull(u.open_orders),
    'Open units': numOrNull(u.open_units),
    'Open FOB': numOrNull(u.open_fob),
    'Received orders': numOrNull(u.recd_orders),
    'Received units': numOrNull(u.recd_units),
    'Received FOB': numOrNull(u.recd_fob),
    'Total FOB': numOrNull(u.total_fob),
  }));
}

// Operator ruling 2: exports mirror the screen. That makes it possible to send someone a 12-row file
// believing it is the whole book, so the active filter travels WITH the file rather than only in the
// reader's memory.
// The PDF table is built HERE, not inline in the handler, so head/body arity is unit-testable. A head
// column added without its body counterpart shifts every column after it silently — a spreadsheet or a
// PDF gives no hint, and this is the artifact most likely to reach a broker (the failure
// ActualCost2DashboardCsv.test.js exists to catch, applied to this export).
export function vendorPdfTable(vendors) {
  return {
    head: [['Vendor', 'Status', 'Appr.', 'Letter', 'BC code(s)', 'Open ord', 'Open FOB', 'Recd ord', 'Recd FOB', 'Total FOB', 'Comments']],
    body: (vendors || []).map((v) => [
      v.vendor_name || '',
      v.status || '',
      yesNo(v.approved),
      yesNo(v.letter_filed),
      // The screen flags a duplicate BC-code claim with a red badge and the Excel gets its own column.
      // Without a marker here the PDF shows the first claimant's full FOB as clean — and First Sale
      // attribution is by LC beneficiary, so this is the omission with money attached.
      (v.bc_codes || []).join(', ') + ((v.code_conflicts || []).length ? ' (conflict)' : ''),
      (v.open_orders || 0).toLocaleString(),
      money(v.open_fob),
      (v.recd_orders || 0).toLocaleString(),
      money(v.recd_fob),
      money(v.total_fob),
      v.comments || '',
    ]),
  };
}

// The screen shows a red alert when the SQL discriminator and composeVendorRollup have drifted: some
// order rows are NOT counted, so every FOB figure is understated. That warning must travel with the
// file. Once a workbook or PDF is detached from this page there is nothing left to say the numbers are
// incomplete, and understated FOB reads as authoritative — worse than no file at all (Codex P1).
// ONE normalization, used by the banner AND both exports. Filtering inside the warning helper alone
// meant a payload like ['', null] rendered an empty red banner on screen while the exports stayed
// silent — the screen crying wolf and the file saying nothing, from the same data (CodeRabbit).
export function normalizeUnknownSources(unknownSources) {
  return (unknownSources || []).filter((s) => s && String(s).trim());
}

export function unknownSourcesWarning(unknownSources) {
  const list = normalizeUnknownSources(unknownSources);
  return list.length
    ? `INCOMPLETE — order rows with an unrecognised source (${list.join(', ')}) are NOT counted. `
      + 'Every FOB figure below is understated. Report this rather than working from these numbers.'
    : null;
}

export function vendorFilterNote(approvedOnly, shown, total) {
  return approvedOnly
    ? `Filtered: approved vendors only — ${shown} of ${total} vendors`
    : `All ${total} vendors (no filter applied)`;
}

// Landscape A4 is 297mm wide; the table sits at a 14mm left margin, so this is the usable text width.
const PDF_TEXT_WIDTH = 269;

const stamp = () => new Date().toISOString().slice(0, 10);

export default function FirstSaleVendors() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [approvedOnly, setApprovedOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const fn = httpsCallable(getFunctions(), 'getFirstSaleVendorOrders', { timeout: 150000 });
      const res = await fn({});
      setData(res.data || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const vendors = useMemo(() => (data?.vendors || []).filter((v) => !approvedOnly || v.approved), [data, approvedOnly]);

  const note = () => vendorFilterNote(approvedOnly, vendors.length, (data?.vendors || []).length);

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    const vendorRows = vendorExportRows(vendors);
    XLSX.utils.book_append_sheet(wb, vendorRows.length
      ? XLSX.utils.json_to_sheet(vendorRows)
      : XLSX.utils.aoa_to_sheet([VENDOR_COLUMNS]), 'Vendors');
    // The unclaimed table is collapsed on screen but it is the expansion-candidate list, so it ships
    // whenever it has rows. It is NOT affected by the approved-only filter — it has no program vendor.
    const unclaimed = unclaimedExportRows(data?.unclaimed);
    if (unclaimed.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(unclaimed), 'Outside program');
    }
    // Provenance on its OWN sheet. Prepending it to the data sheet would push the header off row 1 and
    // break every downstream json_to_sheet / Power Query import the recipient might run.
    const warning = unknownSourcesWarning(data?.unknown_sources);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([
      // FIRST row, so it is the first thing read on the sheet rather than a footnote.
      ...(warning ? [{ Field: '⚠ WARNING', Value: warning }] : []),
      { Field: 'Report', Value: 'First Sale — vendor program' },
      { Field: 'Generated', Value: new Date().toLocaleString() },
      { Field: 'Scope', Value: 'FS / FS3 orders from 1 Jul 2026 onward (CPLAN into a US warehouse)' },
      { Field: 'Filter', Value: note() },
      { Field: 'Vendors in file', Value: vendors.length },
      { Field: 'Outside program', Value: unclaimed.length },
    ]), 'Export info');
    XLSX.writeFile(wb, `first_sale_vendors_${stamp()}.xlsx`);
  };

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const { head, body } = vendorPdfTable(vendors);
    const caption = `${note()} · FS/FS3 from 1 Jul 2026 · generated ${new Date().toLocaleString()}`;
    const warning = unknownSourcesWarning(data?.unknown_sources);
    // Wrap, then size the header block from the ACTUAL line count. A long filter note ran off the
    // right edge and took the timestamp with it — clipping the very provenance the caption exists to
    // carry — and a wrapped warning would have overlapped the table (Codex P2).
    doc.setFontSize(9);
    const capLines = doc.splitTextToSize(caption, PDF_TEXT_WIDTH);
    const warnLines = warning ? doc.splitTextToSize(warning, PDF_TEXT_WIDTH) : [];
    const top = 20 + capLines.length * 4 + (warnLines.length ? warnLines.length * 4 + 2 : 0);
    autoTable(doc, {
      startY: top,
      // startY applies to the FIRST page only; continuation pages begin at margin.top. Without this the
      // didDrawPage caption paints straight over the repeated header and first data row of every page
      // after the first — the page-stamp fix creating a worse bug than the one it solved (Codex P1).
      margin: { top },
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [241, 245, 249], textColor: [51, 65, 85] },
      head,
      body,
      // Money right-aligned so columns read as figures; comments capped so one long note cannot
      // squeeze every other column into unreadability.
      columnStyles: {
        5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' },
        8: { halign: 'right' }, 9: { halign: 'right', fontStyle: 'bold' },
        10: { cellWidth: 60 },
      },
      // Redrawn on EVERY page. autoTable repeats the header row but not free text, so a one-shot
      // doc.text() before the table leaves pages 2+ with no indication they are a filtered extract —
      // and those are exactly the pages that get separated and handed to someone (self-review).
      didDrawPage: () => {
        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text('First Sale — vendor program', 14, 14);
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text(capLines, 14, 20);
        if (warnLines.length) {
          // Red, on EVERY page — a detached page 3 must not read as a complete, trustworthy roster.
          doc.setTextColor(185, 28, 28);
          doc.text(warnLines, 14, 20 + capLines.length * 4 + 2);
          doc.setTextColor(100);
        }
      },
    });
    doc.save(`first_sale_vendors_${stamp()}.pdf`);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600">
        The First Sale vendor program joined by BC code (= ERP LC beneficiary) against
        <span className="font-medium"> FS / FS3 orders from 1 Jul 2026 onward</span> — shown as
        <span className="font-medium"> open</span> (by ship date) and <span className="font-medium">received</span>
        (by receipt date), side by side. Because this is the First Sale book only, an approved vendor whose
        orders are all on other terms reads <span className="font-medium">$0</span> here rather than
        being absent — that gap is reported as "missing-fs" in
        Production → Verification, alongside the FS/FS3 compliance check (CHA-363).
        Edit the program list in Admin → Lookup Manager → First Sale Vendors.
      </div>

      <div className="flex items-center gap-2">
        <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={approvedOnly} onChange={(e) => setApprovedOnly(e.target.checked)} />
          Approved vendors only
        </label>
        {/* Disabled until there is something to export — a click with no rows would hand the operator
            an empty workbook that looks like a real answer. */}
        {/* Excel stays available when the roster filters to nothing but there IS outside-program
            volume — that workbook is not empty, it is the expansion-candidate list, which is exactly
            what someone filtering to zero approved vendors may be after (CodeRabbit). The PDF is
            vendor-only, so it keeps the stricter condition. */}
        <button
          onClick={exportExcel} disabled={!vendors.length && !(data?.unclaimed || []).length}
          title={vendors.length || (data?.unclaimed || []).length ? 'Download the vendor list as Excel' : 'Nothing to export yet'}
          className="ml-auto inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 text-sm font-medium px-2.5 py-1 rounded"
        >
          <FileSpreadsheet className="h-4 w-4" /> Excel
        </button>
        <button
          onClick={exportPdf} disabled={!vendors.length}
          title={vendors.length ? 'Download the vendor list as PDF' : 'Nothing to export yet'}
          className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 text-sm font-medium px-2.5 py-1 rounded"
        >
          <FileText className="h-4 w-4" /> PDF
        </button>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 text-sm font-medium px-2.5 py-1 rounded">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
        </button>
      </div>

      {error && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{error}</div>}
      {loading && !data && <div className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading vendor program…</div>}

      {/* CHA-424 — if the SQL discriminator and composeVendorRollup ever drift, every vendor reads $0
          with nothing else on screen to explain it. Say so rather than let it look like no volume. */}
      {data && normalizeUnknownSources(data.unknown_sources).length > 0 && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
          Some order rows arrived with an unrecognised source ({normalizeUnknownSources(data.unknown_sources).join(', ')}) and are
          <strong> not counted below</strong> — the figures on this screen are incomplete. This means the
          query and the rollup have drifted; report it rather than working from these numbers.
        </div>
      )}
      {data && (
        <>
          {/* Program roster + open-order FOB */}
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="text-slate-500 text-xs bg-slate-50">
                <tr>{['Vendor', 'Status', 'Approved', 'Letter filed', 'BC code(s)', 'Open orders', 'Open FOB', 'Received orders', 'Received FOB', 'Total FOB', 'Comments'].map((h, i) => <th key={i} className="text-left font-medium px-3 py-2">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {vendors.map((v) => (
                  <tr key={v.vendor_name} className="hover:bg-slate-50">
                    <td className="px-3 py-1.5 font-medium">{v.vendor_name}</td>
                    <td className="px-3 py-1.5 text-xs">
                      <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${v.status === 'Complete' ? 'bg-green-100 text-green-800' : v.status === 'In progress' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}`}>{v.status}</span>
                    </td>
                    <td className="px-3 py-1.5">{v.approved ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <span className="text-slate-400 text-xs">No</span>}</td>
                    <td className="px-3 py-1.5">{v.letter_filed ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <span className="text-slate-400 text-xs">—</span>}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">
                      {v.bc_codes.length ? v.bc_codes.join(', ') : <span className="text-amber-700">none yet</span>}
                      {(v.code_conflicts || []).length > 0 && <span title={`Duplicate BC-code claim — volume attributed to the first claimant: ${v.code_conflicts.join('; ')}`} className="ml-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-800 cursor-help">conflict</span>}
                    </td>
                    {/* CHA-424 — open and received kept apart: what is still to come reads differently
                        from what has already landed. Units live in the tooltip to keep the row readable. */}
                    <td className="px-3 py-1.5 tabular-nums text-slate-600" title={`${(v.open_units || 0).toLocaleString()} units`}>{(v.open_orders || 0).toLocaleString()}</td>
                    <td className="px-3 py-1.5 tabular-nums">{money(v.open_fob)}</td>
                    <td className="px-3 py-1.5 tabular-nums text-slate-600" title={`${(v.recd_units || 0).toLocaleString()} units`}>{(v.recd_orders || 0).toLocaleString()}</td>
                    <td className="px-3 py-1.5 tabular-nums">{money(v.recd_fob)}</td>
                    <td className="px-3 py-1.5 tabular-nums font-medium">{money(v.total_fob)}</td>
                    <td className="px-3 py-1.5 text-xs text-slate-500 max-w-[22rem] truncate" title={v.comments}>{v.comments || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Beneficiaries with open volume that no program vendor claims — expansion candidates */}
          {(data.unclaimed || []).length > 0 && (
            <details className="rounded-lg border border-slate-200 bg-white">
              <summary className="px-3 py-2 text-sm font-medium text-slate-600 cursor-pointer">
                Beneficiaries outside the program ({data.unclaimed.length}) — total FOB {money(data.unclaimed.reduce((s, u) => s + (u.total_fob || 0), 0))}
              </summary>
              <div className="overflow-x-auto border-t border-slate-200">
                <table className="min-w-full text-sm">
                  <thead className="text-slate-500 text-xs bg-slate-50">
                    <tr>{['BC code', 'Open orders', 'Open FOB', 'Received orders', 'Received FOB', 'Total FOB'].map((h, i) => <th key={i} className="text-left font-medium px-3 py-2">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.unclaimed.map((u) => (
                      <tr key={u.benef} className="hover:bg-slate-50">
                        <td className="px-3 py-1.5 font-mono text-xs">{u.benef}</td>
                        <td className="px-3 py-1.5 tabular-nums text-slate-600" title={`${(u.open_units || 0).toLocaleString()} units`}>{(u.open_orders || 0).toLocaleString()}</td>
                        <td className="px-3 py-1.5 tabular-nums">{money(u.open_fob)}</td>
                        <td className="px-3 py-1.5 tabular-nums text-slate-600" title={`${(u.recd_units || 0).toLocaleString()} units`}>{(u.recd_orders || 0).toLocaleString()}</td>
                        <td className="px-3 py-1.5 tabular-nums">{money(u.recd_fob)}</td>
                        <td className="px-3 py-1.5 tabular-nums font-medium">{money(u.total_fob)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}
